import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["vertical"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-return-assign */

/* eslint-disable no-plusplus */
import React, { forwardRef, memo, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import cls from 'clsx';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import ConfigProviderContext from '../config-provider/ConfigProviderContext';
import SwiperItem from './SwiperItem';
import useRefState from '../hooks/use-ref-state';
import { bound } from '../utils/bound';
import { devWarning } from '../utils/dev-log';
import { noop } from '../utils';
import { getRect } from '../hooks/use-rect';
var PageIndicator = /*#__PURE__*/memo(function (_ref) {
  var vertical = _ref.vertical,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useContext = useContext(ConfigProviderContext),
      prefixCls = _useContext.prefixCls,
      createNamespace = _useContext.createNamespace;

  var _createNamespace = createNamespace('indicator', prefixCls),
      _createNamespace2 = _slicedToArray(_createNamespace, 1),
      bem = _createNamespace2[0];

  var dots = [];

  for (var i = 0; i < props.total; i++) {
    dots.push( /*#__PURE__*/React.createElement("div", {
      key: i,
      className: cls(bem('dot', {
        active: props.current === i
      }))
    }));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: cls(props.className, bem({
      vertical: vertical
    })),
    style: props.style
  }, dots);
});
var Swiper = /*#__PURE__*/forwardRef(function (props, ref) {
  var _useContext2 = useContext(ConfigProviderContext),
      prefixCls = _useContext2.prefixCls,
      createNamespace = _useContext2.createNamespace;

  var _createNamespace3 = createNamespace('swiper', prefixCls),
      _createNamespace4 = _slicedToArray(_createNamespace3, 1),
      bem = _createNamespace4[0];

  var outerLoop = props.loop,
      autoplay = props.autoplay,
      vertical = props.vertical,
      duration = props.duration;

  var _lock = useRef(false);

  var trackRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      root = _useState2[0],
      setRoot = _useState2[1];

  var _useState3 = useState(props.initialSwipe),
      _useState4 = _slicedToArray(_useState3, 2),
      current = _useState4[0],
      setCurrent = _useState4[1];

  var _useRefState = useRefState(false),
      _useRefState2 = _slicedToArray(_useRefState, 3),
      dragging = _useRefState2[0],
      setDragging = _useRefState2[1],
      draggingRef = _useRefState2[2];

  var axis = useMemo(function () {
    return vertical ? 'y' : 'x';
  }, [vertical]);
  var slideRatio = props.slideSize / 100;
  var offsetRatio = props.trackOffset / 100;
  var computedStyle = useMemo(function () {
    var _objectSpread2;

    return _objectSpread((_objectSpread2 = {}, _defineProperty(_objectSpread2, "--".concat(prefixCls, "-swipe-slide-size"), "".concat(props.slideSize, "%")), _defineProperty(_objectSpread2, "--".concat(prefixCls, "-swipe-track-offset"), "".concat(props.trackOffset, "%")), _objectSpread2), props.style);
  }, [props.style, props.slideSize, props.trackOffset]);
  var axisDistance = useMemo(function () {
    if (!vertical) return 100;
    var rect = getRect(root);
    return rect.height;
  }, [vertical, root]);

  var _useMemo = useMemo(function () {
    var innerCount = 0;
    var innerValidChildren = React.Children.map(props.children, function (child) {
      if (! /*#__PURE__*/React.isValidElement(child)) return null;

      if (child.type !== SwiperItem) {
        devWarning('Swiper', 'The children of `Swiper` must be `Swiper.Item` components.');
        return null;
      }

      innerCount++;
      return child;
    });
    return {
      validChildren: innerValidChildren,
      count: innerCount
    };
  }, [props.children]),
      validChildren = _useMemo.validChildren,
      count = _useMemo.count;

  var loop = useMemo(function () {
    if (count <= 1) return false;
    return outerLoop;
  }, [count, outerLoop]);

  var getSlidePixels = function getSlidePixels() {
    var track = trackRef.current;
    if (!track) return 0;
    var trackPixels = vertical ? track.offsetHeight : track.offsetWidth;
    return trackPixels * props.slideSize / 100;
  };

  var boundIndex = function boundIndex(cur) {
    var min = 0;
    var max = count - 1;

    if (props.stuckAtBoundary) {
      min += (1 - slideRatio - offsetRatio) / slideRatio;
      max -= (1 - slideRatio - offsetRatio) / slideRatio;
    }

    return bound(cur, min, max);
  };

  var _useSpring = useSpring(function () {
    return {
      position: boundIndex(current) * 100,
      config: {
        tension: 200,
        friction: 30,
        duration: duration,
        // eslint-disable-next-line no-cond-assign
        easing: function easing(t) {
          return ((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2;
        }
      },
      onRest: function onRest() {
        if (draggingRef.current) return;
        var rawX = position.get();
        var totalWidth = 100 * count;
        var standardPosition = modulus(rawX, totalWidth);
        if (standardPosition === rawX) return;
        api.start({
          position: standardPosition,
          immediate: true
        });
      }
    };
  }, [count]),
      _useSpring2 = _slicedToArray(_useSpring, 2),
      position = _useSpring2[0].position,
      api = _useSpring2[1];

  var bind = useDrag(function (state) {
    if (_lock.current) return;
    var slidePixels = getSlidePixels();
    if (!slidePixels) return;
    var paramIndex = vertical ? 1 : 0;
    var offset = state.offset[paramIndex];
    var direction = state.direction[paramIndex];
    var velocity = state.velocity[paramIndex];
    setDragging(true);

    if (!state.last) {
      api.start({
        position: offset * 100 / slidePixels,
        immediate: true
      });
    } else {
      var index = Math.round((offset + Math.min(velocity * 2000, slidePixels) * direction) / slidePixels);
      swipeTo(index);
      window.setTimeout(function () {
        setDragging(false);
      });
    }
  }, {
    transform: function transform(_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          x = _ref3[0],
          y = _ref3[1];

      return [-x, -y];
    },
    from: function from() {
      var slidePixels = getSlidePixels();
      return [position.get() / 100 * slidePixels, position.get() / 100 * slidePixels];
    },
    bounds: function bounds() {
      if (loop) return {};
      var slidePixels = getSlidePixels();
      var lowerBound = boundIndex(0) * slidePixels;
      var upperBound = boundIndex(count - 1) * slidePixels;
      return vertical ? {
        top: lowerBound,
        bottom: upperBound
      } : {
        left: lowerBound,
        right: upperBound
      };
    },
    rubberband: true,
    axis: axis,
    preventScroll: !vertical,
    pointer: {
      touch: true
    }
  });

  var renderIndicator = function renderIndicator() {
    if (props.indicator === undefined || props.indicator === true) {
      return /*#__PURE__*/React.createElement("div", {
        className: cls(bem('indicator', {
          vertical: vertical
        }))
      }, /*#__PURE__*/React.createElement(PageIndicator, _extends({}, props.indicatorProps, {
        vertical: vertical,
        total: count,
        current: current
      })));
    }

    if (typeof props.indicator === 'function') {
      return props.indicator(count, current);
    }

    return null;
  };

  var onClickCapture = function onClickCapture(e) {
    if (draggingRef.current) {
      e.stopPropagation();
    }
  };

  function swipeTo(index) {
    var immediate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

    if (loop) {
      var _props$onChange;

      var i = modulus(index, count);
      setCurrent(i);
      (_props$onChange = props.onChange) === null || _props$onChange === void 0 ? void 0 : _props$onChange.call(props, i);
      api.start({
        position: index * 100,
        immediate: immediate
      });
    } else {
      var _props$onChange2;

      var _i = bound(index, 0, count - 1);

      setCurrent(_i);
      (_props$onChange2 = props.onChange) === null || _props$onChange2 === void 0 ? void 0 : _props$onChange2.call(props, _i);
      api.start({
        position: boundIndex(_i) * 100,
        immediate: immediate
      });
    }
  }

  var swipeNext = function swipeNext() {
    swipeTo(Math.round(position.get() / 100) + 1);
  };

  var swipePrev = function swipePrev() {
    swipeTo(Math.round(position.get() / 100) - 1);
  };

  useImperativeHandle(ref, function () {
    return {
      activeIndex: current,
      swipeTo: swipeTo,
      swipeNext: swipeNext,
      swipePrev: swipePrev,
      lock: function lock() {
        _lock.current = true;
      },
      unlock: function unlock() {
        _lock.current = false;
      }
    };
  });
  useEffect(function () {
    if (!autoplay || dragging) return noop;
    var autoplayInterval = typeof autoplay === 'boolean' ? 5000 : autoplay;
    var interval = window.setInterval(function () {
      swipeNext();
    }, autoplayInterval);
    return function () {
      window.clearInterval(interval);
    };
  }, [autoplay, dragging, axisDistance]);

  if (count === 0) {
    devWarning('Swiper', '`Swiper` needs at least one child.');
  }

  return /*#__PURE__*/React.createElement("div", {
    ref: setRoot,
    className: cls(props.className, bem({
      vertical: vertical
    })),
    style: computedStyle
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: trackRef,
    className: cls(bem('track', {
      'allow-touch-move': props.touchable
    })),
    onClickCapture: onClickCapture
  }, props.touchable ? bind() : {}), /*#__PURE__*/React.createElement("div", {
    className: cls(bem('track-inner', {
      vertical: vertical
    }))
  }, React.Children.map(validChildren, function (child, index) {
    var _ref4;

    return /*#__PURE__*/React.createElement(animated.div, {
      className: cls(bem('slide')),
      style: (_ref4 = {}, _defineProperty(_ref4, axis, position.to(function (pos) {
        var finalPosition = -pos + index * 100;
        var totalWidth = count * 100;
        var flagWidth = totalWidth / 2;
        finalPosition = modulus(finalPosition + flagWidth, totalWidth) - flagWidth;
        return "".concat(finalPosition, "%");
      })), _defineProperty(_ref4, "left", "-".concat(index * 100, "%")), _ref4)
    }, child);
  }))), renderIndicator());
}); // defaultProps defined if need

Swiper.defaultProps = {
  initialSwipe: 0,
  touchable: true,
  loop: true,
  autoplay: false,
  slideSize: 100,
  trackOffset: 0,
  stuckAtBoundary: false
};
export default Swiper;

function modulus(value, division) {
  var remainder = value % division;
  return remainder < 0 ? remainder + division : remainder;
}